import type { FC, PropsWithChildren, ReactNode, SyntheticEvent } from "react";

import classNames from "classnames";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import { PictureBehaviour } from "@jti/core-components/components/PictureBehaviour";
import { truncateText } from "@jti/fe-common";
import { Typography } from "@jti/ui/components/Typography";

import { trackCtaClick } from "../../utils/trackCtaClick";

import styles from "./ImageInfoBanner.module.scss";

export type ImageInfoBannerProps = {
  variant?: "default" | "secondary";
  size?: "default" | "small";
  title: string;
  subtitle?: string;
  bottomText: string;
  text?: string;
  backgroundImageSrc: string;
  backgroundImageSrcMedium?: string;
  backgroundImageSrcBig?: string;
  altText?: string;
  url?: string;
  color?: "white" | "black";
  onClick?: ($event: SyntheticEvent) => void;
  hideButton?: boolean;
  grayedOut?: boolean;
  isAnimated?: boolean;
  hasGradient?: boolean;
  buttonVariant?: "button" | "button-transparent" | "chevron-link";
  topRightText?: string;
  pointsProgressBar?: ReactNode;
  isPrizes?: boolean;
  disableButton?: boolean;
  discoverButtonUrl?: string;
  discoverButtonText?: string;
  hasSmallSizedButton?: boolean;
};

const MAX_SUBTITLE_LENGTH = 40;
export const buttonType = (color: string, buttonVariant: string) => {
  const isWhite = color === "white";
  const isTransparent = buttonVariant === "button-transparent";
  if (isWhite) {
    return isTransparent ? "transparent" : "white";
  }
  return isTransparent ? "transparent-black" : "black";
};

export const ImageInfoBanner: FC<PropsWithChildren<ImageInfoBannerProps>> = ({
  variant = "default",
  size = "default",
  title,
  subtitle,
  bottomText,
  text,
  backgroundImageSrc,
  backgroundImageSrcMedium,
  backgroundImageSrcBig,
  altText = "",
  url,
  onClick,
  color = "white",
  hideButton = false,
  grayedOut = false,
  children,
  isAnimated = false,
  hasGradient = false,
  buttonVariant = "button",
  topRightText,
  pointsProgressBar,
  isPrizes,
  disableButton,
  discoverButtonUrl,
  discoverButtonText,
  hasSmallSizedButton = false,
}) => {
  const handleOnClick = ($event: SyntheticEvent) => {
    if (onClick) {
      onClick($event);
    } else {
      trackCtaClick(title, "ImageInfoBanner");
    }
  };

  const classes = classNames(styles.container, {
    [styles.containerSmall]: size === "small",
    "dark-theme": color === "white",
    "light-theme": color === "black",
    [styles.disabled]: grayedOut,
    [styles.animated]: isAnimated,
    [styles.gradient]: hasGradient,
    [styles.secondary]: variant === "secondary",
    [styles.default]: variant === "default",
    [styles.isPrizes]: isPrizes,
  });

  return (
    <div className={classes} data-testid={"imageInfoBanner"}>
      {children}
      {!!topRightText && (
        <div data-testid={"imageInfoBannerTopRightText"} className={styles.topRightText}>
          {topRightText}
        </div>
      )}
      <PictureBehaviour
        sources={[
          {
            srcSet: backgroundImageSrcBig || backgroundImageSrcMedium || backgroundImageSrc,
            media: "(min-width: 1920px)",
          },
          {
            srcSet: backgroundImageSrcMedium || backgroundImageSrcBig || backgroundImageSrc,
            media: "(min-width: 768px)",
          },
        ]}
        imageProps={{
          className: styles.image,
          "data-testid": "imageInfoBannerImage",
          src: backgroundImageSrc,
          alt: altText,
        }}
      />
      <div className={styles.content}>
        {/*
          DESP-96812: Temporary workaround for text sizing.
          Planned to replace with a push block in the builder.
          Currently used across prizes and games cards.
        */}
        {!isPrizes && variant === "default" && buttonVariant === "chevron-link" ? (
          <Typography variant="body1" weight="light" className={styles.newTitle}>
            {title}
          </Typography>
        ) : (
          <Typography variant="h2" weight="light" className={styles.title}>
            {title}
          </Typography>
        )}
        {!!subtitle && (
          <div className={styles.subtitle} data-testid="imageSubtitle">
            {typeof subtitle === "string" ? truncateText(subtitle, MAX_SUBTITLE_LENGTH) : subtitle}
          </div>
        )}
        {!!text && <div className={styles.text}>{text}</div>}
        {pointsProgressBar}
        {buttonVariant === "chevron-link" ? (
          <Button
            className={styles.chevronLink}
            href={url}
            data-testid="ctaLink"
            onClick={handleOnClick}
            variant="text-muted"
            iconAfter={LongArrowForwardIcon}
          >
            {bottomText}
          </Button>
        ) : (
          !hideButton && (
            <Button
              disabled={disableButton}
              size={hasSmallSizedButton ? "sm" : "md"}
              variant={buttonVariant === "button" ? "primary" : "secondary"}
              href={url}
              data-testid="image-info-banner-link"
              onClick={handleOnClick}
            >
              {bottomText}
            </Button>
          )
        )}
        {discoverButtonText && discoverButtonUrl && (
          <Button
            className={styles.discoverBtn}
            href={discoverButtonUrl}
            variant="secondary"
            size={hasSmallSizedButton ? "sm" : "md"}
          >
            {discoverButtonText}
          </Button>
        )}
      </div>
    </div>
  );
};
